/**
 * QuickResolveIssueModal — Push-Mode resolve.
 *
 * Operator stays in-context. The pop-up surfaces:
 *   • Compact resolve form (status + note) for the 80% case.
 *   • Inline "Show details" expander — never navigates away.
 *   • For Material / Sourcing issues: suggested vendor contact cards
 *     (email + phone) so the operator can reach out instantly.
 *   • Quick action chips (Open Job, Open Ordering Queue) that route
 *     only when the operator explicitly chooses to.
 */
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { PushDrawer } from "@/components/quick-actions/PushDrawer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, Mail, Phone, ExternalLink,
  Package, AlertTriangle, Briefcase, ArrowLeft,
  User, MapPin, HardHat, Copy, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { useVendorPO, type Vendor } from "@/data/VendorPOContext";
import { resolveVendorForMaterial } from "@/data/skuMapping";
import { cn } from "@/lib/utils";
import type { Issue, IssueStatus, Job, Contractor } from "@/data/types";
import { useUISkin } from "@/data/UISkinContext";
import { buildPushHandoff } from "@/data/pushHandoffEngine";
import { PushNextStepPanel, type PushNextStep } from "@/components/quick-actions/PushNextStepPanel";
import { InlineSchedulerBlock } from "@/components/quick-actions/InlineSchedulerBlock";
import { IssueBriefCard } from "@/components/quick-actions/IssueBriefCard";
import { StaleContactBanner, staleContactDays } from "@/components/quick-actions/StaleContactBanner";
import { PushProgressTracker } from "@/components/PushProgressTracker";
import { stageKeyToPushStep, type TrackerStageKey } from "@/lib/pushStageNavigation";
import { IssueProgressContextStrip } from "@/components/IssueProgressContextStrip";
import { ContactBlock } from "@/components/quick-actions/ContactBlock";
import { LogInteractionModal } from "@/components/quick-actions/LogInteractionModal";
import { CallDispoModal, type CallDispoResult } from "@/components/quick-actions/CallDispoModal";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { getAvailableActions } from "@/data/situational/availableActions";
import {
  getRequiredInputs,
  buildResolutionPayload,
  validateRequiredInputs,
  type ResolutionPayload,
} from "@/data/situational/requiredInputs";
import { StructuredFieldRenderer, type StructuredValues } from "@/components/situational/StructuredFieldRenderer";
import {
  getDecisionHeader,
  getRiskSignal,
  getOutcomeSummary,
  riskToneClasses,
} from "@/data/situational/decisionClarity";
import {
  SuggestedMoveCard,
  getResolveSuggestedMove,
} from "@/components/quick-actions/SuggestedMoveCard";
import { ChevronDown, ChevronUp } from "lucide-react";

export interface QuickResolveIssueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: Issue | null;
  /** Fired after the issue is committed — payload includes new status & jobId so caller can chain readiness recheck. */
  onSuccess?: (result: { issueId: string; jobId: string; status: IssueStatus }) => void;
  /** Push-Mode chain: when provided, renders a "Back" button that returns the operator to the previous step. Hidden once they've started typing. */
  onBack?: () => void;
  /** Push-Mode chain: operator picked a next step from the success panel. */
  onChooseNextStep?: (step: PushNextStep, job: Job) => void;
}

const NEXT_STATUSES: IssueStatus[] = ["In Progress", "Resolved", "Closed"];

/** Resolve real vendor contact records by fuzzy-matching SKU vendor names. */
function getSuggestedVendors(job: Job | undefined, vendors: Vendor[]): Vendor[] {
  if (!job?.materialsList?.length) return [];
  const seen = new Set<string>();
  const out: Vendor[] = [];
  for (const m of job.materialsList) {
    const skuVendor = resolveVendorForMaterial(m.itemName);
    if (!skuVendor) continue;
    // Fuzzy match: SKU catalog vendor name contains or is contained by VendorPO record.
    const match = vendors.find((v) => {
      const vn = v.vendorName.toLowerCase();
      const sn = skuVendor.name.toLowerCase();
      return vn.includes(sn.split(" ")[0]) || sn.includes(vn.split(" ")[0]);
    });
    if (match && !seen.has(match.vendorId)) {
      seen.add(match.vendorId);
      out.push(match);
    }
  }
  return out.slice(0, 3);
}

export function QuickResolveIssueModal({ open, onOpenChange, issue, onSuccess, onBack, onChooseNextStep }: QuickResolveIssueModalProps) {
  const { setIssues, setJobs, jobs, contractors, issues: allIssues, setFollowUpTasks } = useAppData();
  const { roleInfo } = useRole();
  const { vendors } = useVendorPO();
  const { skin } = useUISkin();
  const cockpit = skin === "cockpit";
  const [status, setStatus] = useState<IssueStatus>("Resolved");
  const [note, setNote] = useState("");
  
  const [dirty, setDirty] = useState(false);
  const [sent, setSent] = useState(false);
  const [resolvedStatus, setResolvedStatus] = useState<IssueStatus | null>(null);
  // Phase 2: Resolution Discipline — structured field values keyed by field.key.
  const [structuredValues, setStructuredValues] = useState<StructuredValues>({});
  // Phase 3: Decision Clarity — capture the normalized payload for the outcome summary.
  const [resolvedPayload, setResolvedPayload] = useState<ResolutionPayload | null>(null);
  // Outreach-Only mode: did the operator actually contact someone?
  // The status auto-derives from this, eliminating a redundant manual pick.
  const [outreachLogged, setOutreachLogged] = useState(false);
  // Track outreach channel separately so structured fields can auto-fill
  // (e.g., "Customer contacted: Yes" the moment Call/Email is tapped on
  // the homeowner; "Crew coordinated: Yes" when crew Call/Email is tapped).
  const [customerOutreachLogged, setCustomerOutreachLogged] = useState(false);
  const [crewOutreachLogged, setCrewOutreachLogged] = useState(false);
  // Escape hatch: operator explicitly chose to override the auto-status.
  const [statusOverride, setStatusOverride] = useState(false);
  // One-Tap Resolve: when the system has a Suggested Move, hide the manual
  // form by default. Operator opens it explicitly via "Different action".
  const [showManual, setShowManual] = useState(false);
  // When the operator taps "Adjust" on the hero, expand the inline scheduler.
  const [expandScheduler, setExpandScheduler] = useState(false);
  // Auto-derived ISO date from a booked visit — feeds the structured
  // "New target date" field so we never show two calendars.
  const [bookedDateIso, setBookedDateIso] = useState<string | null>(null);
  // Call/Email dispo modal — wired to the One-Tap Resolve "Open call dispo".
  const [dispoOpen, setDispoOpen] = useState(false);
  const [dispoChannel, setDispoChannel] = useState<"Phone Call" | "Email">("Phone Call");
  // Linear post-call dispo (situational, scenario-aware). Only the call path
  // uses this; email continues to use the lighter LogInteractionModal.
  const [callDispoOpen, setCallDispoOpen] = useState(false);
  // Job Progress is critical situational awareness when resolving an issue —
  // it answers "where is this job and what does this issue block?" Default open.
  const [showProgress, setShowProgress] = useState(true);

  useEffect(() => {
    if (!issue) return;
    setStatus(issue.status === "Open" ? "In Progress" : "Resolved");
    setNote(issue.resolutionNotes ?? "");
    setDirty(false);
    setSent(false);
    setResolvedStatus(null);
    setStructuredValues({});
    setResolvedPayload(null);
    setOutreachLogged(false);
    setCustomerOutreachLogged(false);
    setCrewOutreachLogged(false);
    setStatusOverride(false);
    setShowManual(false);
    setExpandScheduler(false);
    setBookedDateIso(null);
  }, [issue?.id, open]);

  const job = useMemo(() => (issue ? jobs.find((j) => j.id === issue.jobId) : undefined), [issue, jobs]);

  const isSourcing = useMemo(() => {
    if (!issue) return false;
    return (
      issue.issueType === "Material Issue" ||
      issue.blockerCategory === "Missing Material" ||
      issue.blockerCategory === "Damaged Material" ||
      issue.blockerCategory === "Supplier Delay" ||
      issue.affectedArea === "Materials" ||
      /discontinued|sourc|out of stock|backorder/i.test(issue.description)
    );
  }, [issue]);

  const isScheduling = useMemo(() => {
    if (!issue) return false;
    return (
      issue.issueType === "Scheduling Issue" ||
      issue.affectedArea === "Scheduling" ||
      issue.blockerCategory === "Scheduling Conflict" ||
      /reschedul|conflict|crew unavail|no.show|delay/i.test(issue.description)
    );
  }, [issue]);

  const isContractor = useMemo(() => {
    if (!issue) return false;
    return issue.issueType === "Contractor Issue" || !!issue.relatedContractorId;
  }, [issue]);

  const isAccess = useMemo(() => {
    if (!issue) return false;
    return /access|gate|key|lockbox|on.?site|home.?owner unavail/i.test(issue.description);
  }, [issue]);

  const suggestedVendors = useMemo(
    () => (isSourcing ? getSuggestedVendors(job, vendors) : []),
    [isSourcing, job, vendors],
  );

  // Contractors relevant to this issue: assigned + same-region backups (max 3 total)
  const relevantContractors = useMemo<Contractor[]>(() => {
    if (!job || (!isScheduling && !isContractor)) return [];
    const out: Contractor[] = [];
    const assigned = contractors.find(
      (c) => c.id === issue?.relatedContractorId || c.companyName === job.assignedSubcontractor,
    );
    if (assigned) out.push(assigned);
    const region = job.address?.city;
    const backups = contractors.filter(
      (c) =>
        c.id !== assigned?.id &&
        c.availabilityStatus === "Available" &&
        (!region || c.serviceRegions?.some((r) => r.toLowerCase().includes(region.toLowerCase()))),
    );
    return [...out, ...backups].slice(0, 3);
  }, [job, contractors, isScheduling, isContractor, issue?.relatedContractorId]);

  // Open issues for the success panel's next-step engine,
  // excluding the one we just resolved. Must be declared before any early return.
  const openIssuesAfter = useMemo(
    () =>
      issue
        ? allIssues.filter(
            (i) =>
              i.jobId === issue.jobId &&
              i.id !== issue.id &&
              (i.status === "Open" || i.status === "In Progress"),
          )
        : [],
    [allIssues, issue?.id, issue?.jobId],
  );

  // Phase 1: Surface Intelligence — situational gating + context-aware labels.
  const actions = useMemo(() => getAvailableActions(issue, job), [issue, job]);
  // Phase 2: Resolution Discipline — structured inputs by scenario.
  const requiredInputs = useMemo(() => getRequiredInputs(issue, job), [issue, job]);
  const useStructured = requiredInputs.scenario !== "generic";
  // Phase 3: Decision Clarity — header, risk signal, outcome summary.
  const decisionHeader = useMemo(() => getDecisionHeader(requiredInputs.scenario), [requiredInputs.scenario]);
  const riskSignal = useMemo(
    () => getRiskSignal(issue, job, openIssuesAfter.length),
    [issue, job, openIssuesAfter.length],
  );
  const outcomeSummary = useMemo(
    () => (resolvedPayload ? getOutcomeSummary(resolvedPayload) : null),
    [resolvedPayload],
  );

  // One-Tap Resolve: compute the single suggested move for the hero card.
  // Pure derivation — no side effects, safe in render.
  const suggestedMove = useMemo(() => {
    if (!issue) return null;
    return getResolveSuggestedMove({
      scenario: requiredInputs.scenario,
      isOutreachOnly: actions.isOutreachOnly,
      topVendorName: undefined,
      topContractorName: undefined,
      customerFirstName: job?.customerName?.split(" ")[0],
    });
  }, [issue, requiredInputs.scenario, actions.isOutreachOnly, job?.customerName]);

  /**
   * Auto-derived field values from logged outreach + booked visits.
   * The system already knows whether contact happened or a date was
   * picked — don't ask the CSR. Falls through to whatever the operator
   * manually set if the runtime signal hasn't fired yet.
   */
  const derivedValues = useMemo<Record<string, string | boolean>>(() => {
    const out: Record<string, string | boolean> = {};
    for (const f of requiredInputs.fields) {
      if (!f.derivedFrom) continue;
      if (f.derivedFrom === "outreach.customer" && customerOutreachLogged) out[f.key] = true;
      if (f.derivedFrom === "outreach.contractor" && crewOutreachLogged) out[f.key] = true;
      if (f.derivedFrom === "outreach.vendor" && outreachLogged) out[f.key] = true;
      if (f.derivedFrom === "scheduler.bookedDate" && bookedDateIso) {
        // Format YYYY-MM-DD for native date input compatibility.
        out[f.key] = bookedDateIso.slice(0, 10);
      }
    }
    return out;
  }, [requiredInputs.fields, customerOutreachLogged, crewOutreachLogged, outreachLogged, bookedDateIso]);

  /** Fields the form should actually render — hides anything auto-derived. */
  const visibleFields = useMemo(
    () => requiredInputs.fields.filter((f) => !f.derivedFrom),
    [requiredInputs.fields],
  );

  /** Effective values = manual + auto-derived overlay (auto wins when present). */
  const effectiveValues = useMemo<StructuredValues>(
    () => ({ ...structuredValues, ...derivedValues }),
    [structuredValues, derivedValues],
  );

  if (!issue) return null;

  const handleConfirm = () => {
    // Outreach-Only mode: derive status from the action the operator
    // actually took. No manual pick = no opportunity to misreport.
    const useOutreach = actions.isOutreachOnly && !statusOverride;
    const finalStatus: IssueStatus = useOutreach
      ? (outreachLogged ? actions.autoStatusWithOutreach : actions.autoStatusWithoutOutreach)
      : status;

    // Phase 2: Resolution Discipline — structured scenarios validate fields
    // and produce a normalized summary; generic falls back to free-text note.
    let resolutionText = note.trim();

    if (useStructured && finalStatus === "Resolved") {
      // Validate against EFFECTIVE values (manual + auto-derived from outreach).
      const missing = validateRequiredInputs(requiredInputs, effectiveValues);
      if (missing.length > 0) {
        toast.error(`Complete required fields: ${missing.join(", ")}`);
        return;
      }
      const payload = buildResolutionPayload(requiredInputs, effectiveValues, note);
      resolutionText = payload.summary || resolutionText;
      setResolvedPayload(payload);
    } else if (!useStructured && !useOutreach && finalStatus === "Resolved" && !note.trim()) {
      toast.error("Add a resolution note before resolving");
      return;
    }

    // Outreach-Only without any actual outreach = bail with a nudge,
    // not a silent no-op. Mitigates "I clicked Confirm but did nothing."
    if (useOutreach && !outreachLogged) {
      toast.error("Tap Call or Email above to log an outreach attempt");
      return;
    }

    const now = new Date().toISOString();
    setIssues((prev) =>
      prev.map((i) =>
        i.id === issue.id
          ? {
              ...i,
              status: finalStatus,
              resolutionNotes: resolutionText || i.resolutionNotes,
              dateInProgress: finalStatus === "In Progress" && !i.dateInProgress ? now : i.dateInProgress,
              dateResolved: finalStatus === "Resolved" ? now : i.dateResolved,
              dateClosed: finalStatus === "Closed" ? now : i.dateClosed,
            }
          : i,
      ),
    );
    toast.success(`Issue marked ${finalStatus}`);
    setResolvedStatus(finalStatus);
    setSent(true);
    if (onSuccess) onSuccess({ issueId: issue.id, jobId: issue.jobId, status: finalStatus });
  };

  /**
   * Hand-off: create a tracked FollowUpTask + audit entry on the job.
   * The PushNextStepPanel collects assignee + due date before invoking this.
   */
  const handleHandOffConfirmed = (
    assignee: string,
    dueIso: string,
    selection?: import("./PushHandoffPrompt").PushHandoffSelection,
  ) => {
    if (!job) { onOpenChange(false); return; }
    const { task, activityEntry } = buildPushHandoff({
      job,
      context: {
        reasonFragment: `after issue ${issue.id} resolution`,
        sourceLabel: "Resolve Issue",
      },
      assignee,
      assigneeRoleKey: selection?.roleKey ?? null,
      assigneeUserId: selection?.userId ?? null,
      dueDate: dueIso,
      initiatedBy: roleInfo.label,
    });
    setFollowUpTasks((prev) => [...prev, { ...task, issueId: issue.id }]);
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== job.id ? j : { ...j, activityHistory: [...(j.activityHistory ?? []), activityEntry] },
      ),
    );
    toast.success(`Handed off to ${assignee} — due ${dueIso.slice(0, 10)}`);
    onOpenChange(false);
  };

  // openIssuesAfter is defined above, before the early return.

  /** Append a structured outreach line into the resolution note (no navigation). */
  const logOutreach = (vendor: Vendor, channel: "email" | "phone") => {
    const stamp = new Date().toLocaleString();
    const line = `[${stamp}] Reached out to ${vendor.vendorName} (${vendor.contactName}) via ${channel === "email" ? vendor.email : vendor.phone}`;
    setNote((prev) => (prev ? `${prev}\n${line}` : line));
    if (status === "Resolved") setStatus("In Progress");
    setOutreachLogged(true);
    toast.success(`Outreach logged — ${vendor.vendorName}`);
  };

  const logCrewOutreach = (c: Contractor, channel: "email" | "phone") => {
    const stamp = new Date().toLocaleString();
    const line = `[${stamp}] Reached out to ${c.companyName} (${c.contactName}) via ${channel === "email" ? c.email : c.phone}`;
    setNote((prev) => (prev ? `${prev}\n${line}` : line));
    if (status === "Resolved") setStatus("In Progress");
    setOutreachLogged(true);
    setCrewOutreachLogged(true);
    toast.success(`Outreach logged — ${c.companyName}`);
  };

  const logHomeownerOutreach = (channel: "email" | "phone") => {
    if (!job) return;
    const stamp = new Date().toLocaleString();
    const line = `[${stamp}] Contacted homeowner ${job.customerName} via ${channel === "email" ? job.email : job.phone}`;
    setNote((prev) => (prev ? `${prev}\n${line}` : line));
    if (status === "Resolved") setStatus("In Progress");
    setOutreachLogged(true);
    setCustomerOutreachLogged(true);
    toast.success(`Homeowner contact logged`);
  };

  const copyAddress = () => {
    if (!job?.address) return;
    const full = `${job.address.street}, ${job.address.city}`;
    navigator.clipboard?.writeText(full);
    toast.success("Address copied");
  };

  // Outreach-Only: status derives from action, not a manual pick.
  // Operators can still override via the small "Override status" link.
  const useOutreachStatus = actions.isOutreachOnly && !statusOverride;
  const effectiveStatus: IssueStatus = useOutreachStatus
    ? (outreachLogged ? actions.autoStatusWithOutreach : actions.autoStatusWithoutOutreach)
    : status;

  return (
    <>
    <PushDrawer
      open={open}
      onOpenChange={onOpenChange}
      bodyClassName={cockpit ? "cockpit-surface px-7 py-6 space-y-6" : undefined}
      header={
        cockpit ? (
          <div className="cockpit-surface space-y-2">
            <p className="cockpit-caption uppercase-none">{issue.id} · {issue.severity}</p>
            <h2 className="cockpit-title">Resolve issue</h2>
            {sent && resolvedStatus && (
              <div className="inline-flex items-center gap-2 rounded-full border border-success/30 bg-success/10 px-2.5 py-1 text-[11px] font-semibold text-success">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span>Issue {resolvedStatus.toLowerCase()}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <h2 className="flex items-center gap-2 text-base font-semibold leading-none tracking-tight">
              <CheckCircle2 className="h-4 w-4 text-primary" /> Resolve Issue
            </h2>
            <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
              <span className="font-mono text-[11px]">{issue.id}</span>
              <Badge variant={issue.severity === "Urgent" ? "destructive" : "secondary"} className="text-[10px] px-1.5 py-0">
                {issue.severity}
              </Badge>
              {sent && resolvedStatus && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 border-success/40 bg-success/10 text-success gap-1"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Issue {resolvedStatus.toLowerCase()}
                </Badge>
              )}
            </div>
          </div>
        )
      }
      footer={
        !sent ? (
          <div className="flex items-center gap-2 sm:justify-end">
            {onBack && !dirty && (
              <Button variant="ghost" size="sm" onClick={onBack} className="mr-auto gap-1.5">
                {cockpit ? "Back" : <><ArrowLeft className="h-3.5 w-3.5" /> Back</>}
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-3">
          {/* TOP PRIORITY — stale-contact alert sits above everything when relevant.
              Sources from issue age (fallback) + last contact, so it shows up
              even when the activity log has no record. */}
          {job && (
            <StaleContactBanner
              job={job}
              issue={issue}
              onCallClick={() => logHomeownerOutreach("phone")}
            />
          )}

          {/* Consolidated Issue Brief — customer, stats, reported, detail chips,
              homeowner concern, suggested action — single card, no expander. */}
          {job && (
            <IssueBriefCard
              job={job}
              issue={issue}
              openIssues={openIssuesAfter}
              onPhoneClick={() => logHomeownerOutreach("phone")}
              onEmailClick={() => logHomeownerOutreach("email")}
              onCopyAddress={copyAddress}
            />
          )}

          {/* Job Progress + Issue Context — open by default. The strip names
              the current stage, days parked, and which downstream stages
              this specific issue blocks; the tracker shows the full lifecycle. */}
          {job && (
            <div className="space-y-2">
              <IssueProgressContextStrip job={job} issue={issue} />
              <Collapsible open={showProgress} onOpenChange={setShowProgress}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between rounded-md border bg-muted/20 hover:bg-muted/40 transition-colors px-3 py-1.5 text-[11px] text-muted-foreground"
                  >
                    <span>{showProgress ? "Hide" : "View"} full job progress</span>
                    {showProgress ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <PushProgressTracker
                    job={job}
                    onStageClick={(key) => {
                      onChooseNextStep?.(stageKeyToPushStep(key as TrackerStageKey, job.id), job);
                    }}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Risk/urgency banner — suppressed when StaleContactBanner is already
              showing the same "customer waiting" message above (avoid duplication). */}
          {(() => {
            const stale = job ? staleContactDays(job, issue) : null;
            const bannerShowing = stale !== null && stale >= 3;
            const dupe = bannerShowing && /customer waiting/i.test(riskSignal.message);
            if (dupe) return null;
            const tc = riskToneClasses(riskSignal.tone);
            return (
              <div className={`rounded-md border ${tc.container} px-3 py-2 flex items-center gap-2`}>
                <span className={`h-1.5 w-1.5 rounded-full ${tc.dot}`} aria-hidden />
                <span className={`text-[11px] font-medium ${tc.text}`}>{riskSignal.message}</span>
              </div>
            );
          })()}

          {/* ── ONE-TAP RESOLVE HERO ──
              When the system has a recommended move, surface it FIRST as a
              single approve-or-adjust card. Operators stop describing
              resolutions and start approving them. */}
          {!sent && suggestedMove && !showManual && (
            <SuggestedMoveCard
              move={suggestedMove}
              onDoThis={() => {
                // Scenario-specific one-tap execution paths:
                if (actions.isOutreachOnly) {
                  // Outreach-only → linear, scenario-aware Call Dispo.
                  // Acts as the "call ended" form: situational dropdowns
                  // for outcome + deterministic resolution direction →
                  // issue status. "Other" free-text is always available.
                  setCallDispoOpen(true);
                } else if (suggestedMove.icon === "schedule" || suggestedMove.icon === "service") {
                  // Scheduling/service → expand scheduler so operator confirms slot.
                  setExpandScheduler(true);
                  setShowManual(true);
                } else {
                  // Material / permit / other → reveal manual form pre-focused.
                  setShowManual(true);
                }
              }}
              onAdjust={() => {
                setExpandScheduler(true);
                setShowManual(true);
              }}
              onManual={() => setShowManual(true)}
            />
          )}

          {sent && job ? (
            <PushNextStepPanel
              job={job}
              openIssues={openIssuesAfter}
              headline="Issue resolved — what's next?"
              summary={
                outcomeSummary
                  ?? (resolvedStatus ? `Status set to ${resolvedStatus}${note ? " · note saved" : ""}` : undefined)
              }
              onContinue={(step) => {
                if (onChooseNextStep) onChooseNextStep(step, job);
                else onOpenChange(false);
              }}
              onPause={() => onOpenChange(false)}
              onHandOffConfirmed={handleHandOffConfirmed}
              handoffReasonFragment={`after issue ${issue.id} resolution`}
            />
          ) : (
          <>
          {/* Manual form is hidden by default when a Suggested Move is showing.
              Operator opens it via "Different action" / "Adjust" on the hero. */}
          {(!suggestedMove || showManual) && (
          <>
          {/* Scenario-specific decision header (structured only). */}
          {useStructured && (
            <div className="rounded-md border border-primary/20 bg-primary/[0.03] px-3 py-2.5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{decisionHeader.title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{decisionHeader.description}</p>
              </div>
              {suggestedMove && (
                <button
                  type="button"
                  onClick={() => setShowManual(false)}
                  className="text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline shrink-0 inline-flex items-center gap-0.5"
                >
                  <ChevronUp className="h-3 w-3" /> Collapse
                </button>
              )}
            </div>
          )}

          {/* Scheduling / Contractor resources — context-aware label via getAvailableActions */}
          {actions.showContractorContacts && relevantContractors.length > 0 && (
            <div className="rounded-md border border-primary/20 bg-primary/[0.03] p-3 space-y-2">
              <div className="flex items-center gap-1.5">
                <HardHat className="h-3.5 w-3.5 text-primary" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-primary">
                  {actions.contractorBlockLabel}
                </p>
              </div>
              <div className="space-y-1.5">
                {relevantContractors.map((c) => (
                  <div key={c.id} className="rounded border bg-background px-2.5 py-2">
                    <ContactBlock
                      name={c.companyName}
                      subtitle={`${c.contactName} · ${c.availabilityStatus}`}
                      phone={c.phone}
                      email={c.email}
                      emailSubject={`${isScheduling ? "Schedule change" : "Coordination"} — ${job?.customerName ?? issue.jobId}`}
                      onPhoneClick={() => logCrewOutreach(c, "phone")}
                      onEmailClick={() => logCrewOutreach(c, "email")}
                      badge={
                        c.companyName === job?.assignedSubcontractor ? (
                          <Badge variant="secondary" className="text-[9px] px-1 py-0">Assigned</Badge>
                        ) : undefined
                      }
                    />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground italic">
                Tap Call or Email — outreach is auto-logged into the resolution note.
              </p>
            </div>
          )}

          {/* Quick navigation — single quiet link. All "Issue Detail" content
              (blocker chips, homeowner concern, suggested action) is now
              integrated directly into IssueBriefCard above, so the expander
              is gone — one less click, no duplication. */}
          <div className="flex flex-wrap gap-1.5">
            <Link
              to={`/jobs/${issue.jobId}`}
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border bg-muted/40 hover:bg-muted transition-colors"
            >
              <Briefcase className="h-3 w-3" /> Open job
              <ExternalLink className="h-2.5 w-2.5 opacity-60" />
            </Link>
            {isSourcing && (
              <Link
                to="/ordering-queue"
                onClick={() => onOpenChange(false)}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border bg-muted/40 hover:bg-muted transition-colors"
              >
                <Package className="h-3 w-3" /> Ordering queue
                <ExternalLink className="h-2.5 w-2.5 opacity-60" />
              </Link>
            )}
          </div>

          {/* Vendor contacts — sourcing-only, gated by getAvailableActions */}
          {actions.showVendorContacts && suggestedVendors.length > 0 && (
            <div className="rounded-md border border-primary/20 bg-primary/[0.03] p-3 space-y-2">
              <div className="flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5 text-primary" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-primary">
                  Suggested vendors — reach out
                </p>
              </div>
              <div className="space-y-1.5">
                {suggestedVendors.map((v) => (
                  <div key={v.vendorId} className="rounded border bg-background px-2.5 py-2">
                    <ContactBlock
                      name={v.vendorName}
                      subtitle={`${v.contactName} · ${v.orderingMethod}`}
                      phone={v.phone}
                      email={v.email}
                      emailSubject={`Sourcing request — ${job?.customerName ?? issue.jobId}`}
                      onPhoneClick={() => logOutreach(v, "phone")}
                      onEmailClick={() => logOutreach(v, "email")}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground italic">
                Tap Call or Email — we'll log it into the resolution note automatically.
              </p>
            </div>
          )}

          {/* Inline scheduler — gated by getAvailableActions; only shown when a visit logically helps. */}
          {job && actions.showScheduler && (
            <InlineSchedulerBlock
              job={job}
              issue={issue}
              triggerLabel={actions.schedulerLabel}
              triggerSubLabel={actions.schedulerSubLabel}
              defaultExpanded={expandScheduler}
              onScheduled={(evt) => {
                const stamp = new Date().toLocaleString();
                const line = `[${stamp}] Booked ${evt.eventType.replace("_", " ")} on ${new Date(evt.startTime).toLocaleDateString()} with ${evt.crewName}`;
                setNote((prev) => (prev ? `${prev}\n${line}` : line));
                if (status === "Resolved") setStatus("In Progress");
                // Auto-fills the structured "New target date" field —
                // eliminates the duplicate calendar.
                setBookedDateIso(evt.startTime);
                setDirty(true);
              }}
            />
          )}

          {/* Status — situational.
              Outreach-Only mode hides the manual picker; the dispo of the
              call/email drives status automatically. An "Override" link
              is the explicit escape hatch for the rare edge case. */}
          {useOutreachStatus ? (
            <div className="rounded-md border bg-muted/10 px-3 py-2.5 flex items-start gap-2.5">
              <CheckCircle2
                className={cn(
                  "h-4 w-4 mt-0.5 shrink-0",
                  outreachLogged ? "text-success" : "text-muted-foreground",
                )}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium">
                  {outreachLogged
                    ? `Status will move to In Progress on save`
                    : `Status updates after you log a call or email`}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {outreachLogged
                    ? "We'll mark Resolved later, after the dispo confirms it's actually closed."
                    : "Tap Call or Email above — outreach is auto-logged into the resolution note."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStatusOverride(true)}
                className="text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline shrink-0"
              >
                Override
              </button>
            </div>
          ) : (
            <div>
              <Label htmlFor="qri-status" className="text-xs">
                New status
                {actions.isOutreachOnly && statusOverride && (
                  <button
                    type="button"
                    onClick={() => setStatusOverride(false)}
                    className="ml-2 text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                  >
                    Use auto-status
                  </button>
                )}
              </Label>
              <Select value={status} onValueChange={(v) => { setStatus(v as IssueStatus); setDirty(true); }}>
                <SelectTrigger id="qri-status" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NEXT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Phase 2: Resolution Discipline — structured fields by scenario, generic falls back to free-text. */}
          {useStructured ? (
            <>
              <div className="rounded-md border bg-muted/10 p-3 space-y-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                  Resolution details
                </p>
                <StructuredFieldRenderer
                  fields={visibleFields}
                  values={structuredValues}
                  onChange={(key, value) => {
                    setStructuredValues((prev) => ({ ...prev, [key]: value }));
                    setDirty(true);
                  }}
                  idPrefix={`qri-${issue.id}`}
                />

                {/* Auto-derived chips — what the system already knows from
                    logged outreach. Replaces redundant Yes/No questions. */}
                {requiredInputs.fields.some((f) => f.derivedFrom) && (
                  <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/40 mt-1">
                    {requiredInputs.fields
                      .filter((f) => f.derivedFrom)
                      .map((f) => {
                        const v = derivedValues[f.key];
                        const auto = v !== undefined && v !== false && v !== "";
                        const display = typeof v === "string" ? v : auto ? "Yes — auto-logged" : "Awaiting outreach";
                        return (
                          <span
                            key={f.key}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px]",
                              auto
                                ? "border-success/30 bg-success/10 text-success"
                                : "border-dashed border-border bg-muted/30 text-muted-foreground",
                            )}
                          >
                            {auto && <CheckCircle2 className="h-3 w-3" />}
                            <span>{f.label}:</span>
                            <span className="font-medium">{display}</span>
                          </span>
                        );
                      })}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="qri-note" className="text-xs">
                  {requiredInputs.noteLabel} <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="qri-note"
                  placeholder={requiredInputs.notePlaceholder}
                  value={note}
                  onChange={(e) => { setNote(e.target.value); setDirty(true); }}
                  rows={2}
                  className="mt-1 resize-none text-xs"
                />
              </div>
            </>
          ) : (
            <div>
              <Label htmlFor="qri-note" className="text-xs">
                Resolution note {status === "Resolved" && <span className="text-destructive">*</span>}
              </Label>
              <Textarea
                id="qri-note"
                placeholder="What was done to resolve this?"
                value={note}
                onChange={(e) => { setNote(e.target.value); setDirty(true); }}
                rows={3}
                className="mt-1 resize-none text-xs"
              />
            </div>
          )}
          </>
          )}
          </>
          )}
        </div>
      </PushDrawer>

      {/* Call/Email Dispo capture — outreach IS the resolution.
          When the dispo is logged, we mirror its outcome into the resolution
          note + flip outreachLogged so the auto-status moves to In Progress. */}
      {job && (
        <LogInteractionModal
          open={dispoOpen}
          onOpenChange={setDispoOpen}
          customerName={job.customerName}
          relatedJobId={job.id}
          defaultType={dispoChannel}
          onLogged={(entry) => {
            setNote((prev) => (prev ? `${prev}\n[${entry.date} ${entry.time}] ${entry.commType} — ${entry.summary}` : `[${entry.date} ${entry.time}] ${entry.commType} — ${entry.summary}`));
            setOutreachLogged(true);
            setCustomerOutreachLogged(true);
            setDirty(true);
          }}
        />
      )}

      {/* Linear post-call dispo — situational dropdowns + deterministic
          status mapping. Used by the One-Tap Resolve outreach-only path so
          downstream automations have precise, structured inputs. */}
      {job && (
        <CallDispoModal
          open={callDispoOpen}
          onOpenChange={setCallDispoOpen}
          issue={issue}
          customerName={job.customerName}
          relatedJobId={job.id}
          onLogged={(result: CallDispoResult) => {
            // Mirror dispo outcome into the resolution note (audit trail).
            const stamp = `${result.entry.date} ${result.entry.time}`;
            const line = `[${stamp}] Phone Call — ${result.summary}`;
            setNote((prev) => (prev ? `${prev}\n${line}` : line));

            // Mark outreach as performed so any downstream auto-status logic
            // sees the call happened (eliminates "I confirmed but did nothing").
            setOutreachLogged(true);
            setCustomerOutreachLogged(true);
            setDirty(true);

            // Apply the deterministic status from the chosen direction +
            // commit the issue update immediately so the operator sees
            // the resolved indicator without a second click.
            const finalStatus = result.status;
            const now = new Date().toISOString();
            setIssues((prev) =>
              prev.map((i) =>
                i.id === issue.id
                  ? {
                      ...i,
                      status: finalStatus,
                      resolutionNotes: line,
                      dateInProgress:
                        finalStatus === "In Progress" && !i.dateInProgress
                          ? now
                          : i.dateInProgress,
                      dateResolved:
                        finalStatus === "Resolved" ? now : i.dateResolved,
                      dateClosed:
                        finalStatus === "Closed" ? now : i.dateClosed,
                    }
                  : i,
              ),
            );
            setStatus(finalStatus);
            setResolvedStatus(finalStatus);
            setSent(true);
            if (onSuccess)
              onSuccess({ issueId: issue.id, jobId: issue.jobId, status: finalStatus });
          }}
        />
      )}
    </>
  );
}
